/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  message,
  Table,
  Button,
  Input,
  Tag,
  Card,
  Alert,
  Modal,
  Badge,
  Spin,
  Space,
  List,
  Typography,
  Avatar,
  Drawer,
  Divider,
} from "antd";
import {
  SearchOutlined,
  QrcodeOutlined,
  CheckCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { ticketsApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";

const { Text, Title } = Typography;

export const TicketsList = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({ page: 1, limit: 10 });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const userPermissions = currentUser?.permissions || [];

  // Check permissions
  const canViewTickets = hasPermission(userPermissions, "tickets.view");
  const canCheckInTickets = hasPermission(userPermissions, "tickets.check_in");

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (canViewTickets) {
      fetchTickets();
    }
    setIsCheckingPermission(false);
  }, [filters, debouncedSearch, canViewTickets]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      console.log("🎫 Fetching tickets with filters:", {
        page: filters.page,
        limit: filters.limit,
        search: debouncedSearch,
      });
      
      const query = {
        page: filters.page,
        limit: filters.limit,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      };
      
      const r = await ticketsApi.getAll(query);
      console.log("📊 Tickets API Response:", {
        total: r.total,
        dataLength: (r.data || []).length,
        hasSearch: !!debouncedSearch,
      });
      
      let allTickets = r.data || [];
      
      // Client-side fallback filtering
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        allTickets = allTickets.filter(ticket => 
          ticket.ticketNumber?.toLowerCase().includes(searchLower) ||
          ticket.reservation?.user?.firstName?.toLowerCase().includes(searchLower) ||
          ticket.reservation?.user?.lastName?.toLowerCase().includes(searchLower) ||
          ticket.reservation?.tripDate?.trip?.name?.toLowerCase().includes(searchLower)
        );
        console.log(`🔍 Client-side filtered from ${r.data?.length || 0} to ${allTickets.length}`);
      }
      
      setTickets(allTickets);
      
      let adjustedTotal = r.total || allTickets.length;
      if (debouncedSearch) {
        adjustedTotal = allTickets.length;
      }
      setTotalItems(adjustedTotal);
      
      setSearching(false);
    } catch (error) {
      console.error("❌ Error fetching tickets:", error.response?.data || error.message);
      if (error.response?.status === 403) {
        message.error("You don't have permission to view tickets");
      } else {
        const errorMsg = error.response?.data?.message || error.message || "Failed to load tickets";
        message.error(`Tickets error: ${errorMsg}`);
      }
      setSearching(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setFilters((f) => ({ ...f, page: 1 }));
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setSearching(true);
    }, 300);
  }, []);

  const handleCheckIn = async (ticketId) => {
    if (!canCheckInTickets) {
      message.error("You don't have permission to check in tickets");
      return;
    }

    try {
      await ticketsApi.checkIn(ticketId);
      message.success("Checked in!");
      fetchTickets();
      if (drawerVisible) setDrawerVisible(false);
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to check in tickets");
      } else {
        message.error(e.response?.data?.message || "Check-in failed");
      }
    }
  };

  // Mobile card component
  const TicketCard = ({ ticket, onPress }) => {
    const name = ticket.reservation?.user
      ? `${ticket.reservation.user.firstName} ${ticket.reservation.user.lastName}`
      : ticket.reservation?.guestName || "Guest";

    const getStatusTag = () => {
      if (ticket.isCheckedIn)
        return (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Checked In
          </Tag>
        );
      if (ticket.isCancelled) return <Tag color="red">Cancelled</Tag>;
      const rs = ticket.reservation?.status;
      if (rs === "cancelled") return <Tag color="red">Cancelled</Tag>;
      if (rs === "refunded") return <Tag color="orange">Refunded</Tag>;
      if (rs === "pending") return <Tag color="default">Pending Payment</Tag>;
      return <Tag color="volcano">Active</Tag>;
    };

    const isCheckInAllowed = () => {
      const rs = ticket.reservation?.status;
      return (
        !ticket.isCheckedIn &&
        !ticket.isCancelled &&
        rs !== "cancelled" &&
        rs !== "refunded" &&
        rs !== "pending"
      );
    };

    return (
      <Card
        style={{
          marginBottom: 12,
          borderRadius: 12,
          cursor: "pointer",
          transition: "all 0.3s ease",
        }}
        hoverable
        onClick={() => onPress(ticket)}
        bodyStyle={{ padding: "16px" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div>
            <code
              style={{
                background: "#f0f4ff",
                padding: "4px 8px",
                borderRadius: 4,
                color: "#7B4D12",
                fontSize: 12,
                fontFamily: "monospace",
              }}
            >
              {ticket.ticketNumber}
            </code>
          </div>
          {getStatusTag()}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <Avatar size={40} style={{ backgroundColor: "#7B4D12" }}>
            {name.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: 14, display: "block" }}>
              {name}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {ticket.reservation?.tripDate?.trip?.name || "-"}
            </Text>
          </div>
        </div>

        <Divider style={{ margin: "12px 0" }} />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div>
            <CalendarOutlined
              style={{ fontSize: 12, color: "#8c8c8c", marginRight: 6 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {ticket.reservation?.tripDate?.date || "-"}
            </Text>
          </div>
          <div>
            <EnvironmentOutlined
              style={{ fontSize: 12, color: "#8c8c8c", marginRight: 6 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {ticket.reservation?.deck?.name || "-"}
            </Text>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <UserOutlined
              style={{ fontSize: 12, color: "#8c8c8c", marginRight: 6 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Seats: {ticket.reservation?.seats || 0}
            </Text>
          </div>
          {canCheckInTickets && isCheckInAllowed() && (
            <Button
              size="small"
              type="primary"
              style={{ backgroundColor: "#7B4D12", borderColor: "#7B4D12" }}
              onClick={(e) => {
                e.stopPropagation();
                handleCheckIn(ticket.id);
              }}
            >
              Check In
            </Button>
          )}
        </div>
      </Card>
    );
  };

  // Mobile ticket detail drawer
  const TicketDetailDrawer = ({ ticket, open, onClose }) => {
    if (!ticket) return null;

    const name = ticket.reservation?.user
      ? `${ticket.reservation.user.firstName} ${ticket.reservation.user.lastName}`
      : ticket.reservation?.guestName || "Guest";

    const getStatusInfo = () => {
      if (ticket.isCheckedIn)
        return { label: "Checked In", color: "#059669", bg: "#dcfce7" };
      if (ticket.isCancelled)
        return { label: "Cancelled", color: "#dc2626", bg: "#fee2e2" };
      const rs = ticket.reservation?.status;
      if (rs === "cancelled")
        return { label: "Cancelled", color: "#dc2626", bg: "#fee2e2" };
      if (rs === "refunded")
        return { label: "Refunded", color: "#d97706", bg: "#fef3c7" };
      if (rs === "pending")
        return { label: "Pending Payment", color: "#6b7280", bg: "#f3f4f6" };
      return { label: "Active", color: "#7B4D12", bg: "#F5ECD7" };
    };

    const status = getStatusInfo();
    const isCheckInAllowed = () => {
      const rs = ticket.reservation?.status;
      return (
        !ticket.isCheckedIn &&
        !ticket.isCancelled &&
        rs !== "cancelled" &&
        rs !== "refunded" &&
        rs !== "pending"
      );
    };

    const DetailRow = ({ label, value }) => (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 0",
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          {label}
        </Text>
        <Text strong>{value ?? "—"}</Text>
      </div>
    );

    return (
      <Drawer
        title={
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Ticket Details
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {ticket.ticketNumber}
            </Text>
          </div>
        }
        placement="bottom"
        onClose={onClose}
        open={open}
        height="auto"
        closable={true}
        bodyStyle={{ padding: "16px" }}
      >
        <div style={{ marginBottom: 20 }}>
          <span
            style={{
              padding: "4px 12px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              background: status.bg,
              color: status.color,
            }}
          >
            {status.label}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <Avatar size={48} style={{ backgroundColor: "#7B4D12" }}>
            {name.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: 16, display: "block" }}>
              {name}
            </Text>
            <Text type="secondary">
              {ticket.reservation?.tripDate?.trip?.name || "-"}
            </Text>
          </div>
        </div>

        <DetailRow label="Date" value={ticket.reservation?.tripDate?.date} />
        <DetailRow
          label="Time"
          value={
            ticket.reservation?.tripDate?.trip?.startTime &&
            ticket.reservation?.tripDate?.trip?.endTime
              ? `${ticket.reservation.tripDate.trip.startTime} – ${ticket.reservation.tripDate.trip.endTime}`
              : "—"
          }
        />
        <DetailRow label="Deck" value={ticket.reservation?.deck?.name} />
        <DetailRow label="Seats" value={ticket.reservation?.seats} />
        <DetailRow label="Status" value={status.label} />

        {canCheckInTickets && isCheckInAllowed() && (
          <Button
            type="primary"
            style={{
              backgroundColor: "#7B4D12",
              borderColor: "#7B4D12",
              width: "100%",
              marginTop: 16,
            }}
            icon={<CheckCircleOutlined />}
            onClick={() => handleCheckIn(ticket.id)}
          >
            Check In Passenger
          </Button>
        )}

        {ticket.isCheckedIn && ticket.checkedInAt && (
          <Text
            type="secondary"
            style={{
              fontSize: 12,
              display: "block",
              textAlign: "center",
              marginTop: 12,
            }}
          >
            Checked in at {new Date(ticket.checkedInAt).toLocaleString()}
          </Text>
        )}
      </Drawer>
    );
  };

  const columns = [
    {
      title: "Ticket #",
      dataIndex: "ticketNumber",
      key: "ticketNumber",
      render: (n) => (
        <code
          style={{
            background: "#f0f4ff",
            padding: "2px 8px",
            borderRadius: 4,
            color: "#7B4D12",
          }}
        >
          {n}
        </code>
      ),
    },
    {
      title: "Passenger",
      key: "passenger",
      render: (_, r) => {
        const name = r.reservation?.user
          ? `${r.reservation.user.firstName} ${r.reservation.user.lastName}`
          : r.reservation?.guestName || "Guest";
        return name;
      },
    },
    {
      title: "Trip",
      key: "trip",
      render: (_, r) => r.reservation?.tripDate?.trip?.name || "-",
    },
    {
      title: "Date",
      key: "date",
      render: (_, r) => r.reservation?.tripDate?.date || "-",
    },
    {
      title: "Deck",
      key: "deck",
      render: (_, r) => r.reservation?.deck?.name || "-",
    },
    { title: "Seats", key: "seats", render: (_, r) => r.reservation?.seats },
    {
      title: "Status",
      key: "status",
      render: (_, r) => {
        if (r.isCheckedIn)
          return (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Checked In
            </Tag>
          );
        if (r.isCancelled) return <Tag color="red">Cancelled</Tag>;
        const rs = r.reservation?.status;
        if (rs === "cancelled") return <Tag color="red">Cancelled</Tag>;
        if (rs === "refunded") return <Tag color="orange">Refunded</Tag>;
        if (rs === "pending") return <Tag color="default">Pending Payment</Tag>;
        return <Tag color="volcano">Active</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => {
        if (!canCheckInTickets) return null;

        const isInvalid =
          r.isCancelled ||
          r.isCheckedIn ||
          r.reservation?.status === "cancelled" ||
          r.reservation?.status === "refunded" ||
          r.reservation?.status === "pending";

        if (isInvalid) return null;

        return (
          <Button
            size="small"
            type="primary"
            style={{ backgroundColor: "#7B4D12", borderColor: "#7B4D12" }}
            onClick={() => handleCheckIn(r.id)}
          >
            Check In
          </Button>
        );
      },
    },
  ];

  // Show loading while checking permissions
  if (isCheckingPermission) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div className="text-center py-5">
              <Spin size="large" />
              <p className="mt-2">Checking permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Permission check for viewing the page
  if (!canViewTickets) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to view tickets.</p>
                  <p className="mt-2">
                    <strong>Required permission:</strong> tickets.view
                  </p>
                  <p className="mt-2">
                    <strong>Your permissions:</strong>{" "}
                    {userPermissions.length > 0
                      ? userPermissions.join(", ")
                      : "None"}
                  </p>
                </div>
              }
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div
                  className="content-page-header"
                  style={{
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "center",
                    gap: isMobile ? 12 : 0,
                  }}
                >
                  <h5 style={{ marginBottom: isMobile ? 8 : 0 }}>Tickets</h5>
                  {canCheckInTickets && (
                    <Button
                      icon={<QrcodeOutlined />}
                      onClick={() => history.push("/tickets/scan")}
                      size={isMobile ? "small" : "middle"}
                    >
                      Scan QR
                    </Button>
                  )}
                </div>
              </div>

              {canViewTickets && (
                <div className="row mb-4">
                  <div className="col-md-4">
                    <div className="input-block mb-0">
                      <label
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#6b7280",
                          textTransform: "uppercase",
                          letterSpacing: ".04em",
                        }}
                      >
                        🔍 Search Tickets
                      </label>
                      <Input.Search
                        placeholder={searching ? "Searching..." : "Search by ticket #, passenger, trip..."}
                        onSearch={handleSearch}
                        enterButton={<SearchOutlined />}
                        loading={searching}
                        allowClear
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div style={{ height: "72px", display: "flex", alignItems: "flex-end" }}></div>
                  </div>
                </div>
              )}
              
              {!canCheckInTickets && (
                <Alert
                  message="View Only Mode"
                  description="You can view tickets but cannot check in passengers. Contact an administrator for full access."
                  type="info"
                  showIcon
                  className="mb-4"
                />
              )}

              {isMobile ? (
                // Mobile view: Card-based list
                <>
                  {loading ? (
                    <div style={{ textAlign: "center", padding: "40px 0" }}>
                      <Spin />
                    </div>
                  ) : tickets.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px 0" }}>
                      <Alert message="No tickets found" type="info" showIcon />
                    </div>
                  ) : (
                    <>
                      <List
                        dataSource={tickets}
                        renderItem={(ticket) => (
                          <TicketCard
                            ticket={ticket}
                            onPress={(t) => {
                              setSelectedTicket(t);
                              setDrawerVisible(true);
                            }}
                          />
                        )}
                      />
                      <TicketDetailDrawer
                        ticket={selectedTicket}
                        open={drawerVisible}
                        onClose={() => setDrawerVisible(false)}
                      />
                      <div style={{ marginTop: 16, textAlign: "center" }}>
                        <Space>
                          <Button
                            size="small"
                            disabled={filters.page === 1}
                            onClick={() =>
                              setFilters({ ...filters, page: filters.page - 1 })
                            }
                          >
                            Previous
                          </Button>
                          <Text type="secondary">
                            Page {filters.page} of{" "}
                            {Math.ceil(totalItems / filters.limit)}
                          </Text>
                          <Button
                            size="small"
                            disabled={
                              filters.page * filters.limit >= totalItems
                            }
                            onClick={() =>
                              setFilters({ ...filters, page: filters.page + 1 })
                            }
                          >
                            Next
                          </Button>
                        </Space>
                      </div>
                    </>
                  )}
                </>
              ) : (
                // Desktop view: Table layout
                <Table
                  columns={columns}
                  dataSource={tickets}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    current: filters.page,
                    pageSize: filters.limit,
                    total: totalItems,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} tickets`,
                  }}
                  onChange={(p) =>
                    setFilters((f) => ({
                      ...f,
                      page: p.current,
                      limit: p.pageSize,
                    }))
                  }
                  scroll={{ x: 900 }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ScanQR = () => {
  const [menu, setMenu] = useState(false);
  const [qrInput, setQrInput] = useState("");
  const [ticketNum, setTicketNum] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("camera");
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const userPermissions = currentUser?.permissions || [];

  // Check permissions
  const canCheckInTickets = hasPermission(userPermissions, "tickets.check_in");
  const canViewOwnTickets = hasPermission(userPermissions, "tickets.self_view");
  const canViewAllTickets = hasPermission(userPermissions, "tickets.view");
  const canAccessScan = canCheckInTickets;

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const jsQRRef = useRef(null);
  const inputRef = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Load jsQR from jsdelivr (fallback for non-BarcodeDetector browsers) ───
  useEffect(() => {
    if (window.jsQR) {
      jsQRRef.current = window.jsQR;
      return;
    }
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js";
    s.onload = () => {
      jsQRRef.current = window.jsQR;
    };
    document.head.appendChild(s);
  }, []);

  // ── Auto-start camera when tab = camera ───────────────────────────────────
  useEffect(() => {
    if (tab === "camera") startCamera();
    else stopCamera();
    return () => stopCamera();
  }, [tab]);

  // ── Focus manual input ─────────────────────────────────────────────────────
  useEffect(() => {
    if (tab !== "camera") setTimeout(() => inputRef.current?.focus(), 100);
  }, [tab]);

  // ── Camera helpers ─────────────────────────────────────────────────────────
  const startCamera = async () => {
    if (!canAccessScan) return;

    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: isMobile ? 720 : 1280 },
          height: { ideal: isMobile ? 480 : 720 },
        },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      video.srcObject = stream;
      setCameraOn(true);

      video.oncanplay = () => {
        if (!videoRef.current) return;
        video
          .play()
          .then(() => startScanLoop())
          .catch(() => {});
        video.oncanplay = null;
      };
    } catch (e) {
      setCameraError(
        e.name === "NotAllowedError"
          ? "Camera access denied. Please allow camera access in your browser settings."
          : "Could not open camera: " + e.message,
      );
    }
  };

  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.oncanplay = null;
      videoRef.current.srcObject = null;
    }

    setCameraOn(false);
    setScanning(false);
  };

  // ── Toggle torch ───────────────────────────────────────────────────
  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch {
      message.warning("Torch not supported on this device");
    }
  };

  // ── Scan loop ───────────────────────────────────────────────────────────
  const startScanLoop = () => {
    setScanning(true);
    const scan = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState < 2) {
        rafRef.current = requestAnimationFrame(scan);
        return;
      }
      const ctx = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      if (window.BarcodeDetector) {
        new window.BarcodeDetector({ formats: ["qr_code"] })
          .detect(canvas)
          .then((codes) => {
            if (codes.length > 0) handleQRDetected(codes[0].rawValue);
            else rafRef.current = requestAnimationFrame(scan);
          })
          .catch(() => {
            rafRef.current = requestAnimationFrame(scan);
          });
      } else if (jsQRRef.current) {
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQRRef.current(imgData.data, canvas.width, canvas.height);
        if (code) handleQRDetected(code.data);
        else rafRef.current = requestAnimationFrame(scan);
      } else {
        rafRef.current = requestAnimationFrame(scan);
      }
    };
    rafRef.current = requestAnimationFrame(scan);
  };

  // ── Called when a QR code frame is detected ────────────────────────────────
  const handleQRDetected = (raw) => {
    if (loading) return;
    cancelAnimationFrame(rafRef.current);
    setScanning(false);
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.fillStyle = "rgba(123,77,18,0.25)";
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    doScan(raw);
  };

  // ── Resume scanning after error ────────────────────────────────────────────
  const resumeScan = () => {
    setError(null);
    setScanning(true);
    startScanLoop();
  };

  // ── Core scan / check-in ──────────────────────────────────────────────────
  const doScan = async (raw) => {
    if (!canCheckInTickets) {
      message.error("You don't have permission to check in passengers");
      return;
    }

    const payload = (raw || qrInput).trim();
    if (!payload) return;
    if (!payload.startsWith("{")) {
      setError("Invalid QR code — expected JSON payload.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setResult(null);
      setPreview(null);
      const r = await ticketsApi.scan(payload);
      setResult(r);
      setQrInput("");
      message.success("Passenger checked in!");
    } catch (e) {
      if (e.response?.status === 403) {
        setError("You don't have permission to check in passengers");
      } else {
        const msg = e.response?.data?.message;
        setError(Array.isArray(msg) ? msg.join(", ") : msg || "Scan failed");
      }
    } finally {
      setLoading(false);
      if (tab === "qr") setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // ── Ticket lookup by number ────────────────────────────────────────────────
  const handleLookup = async () => {
    if (!canCheckInTickets) {
      message.error("You don't have permission to check in passengers");
      return;
    }

    const num = ticketNum.trim().toUpperCase();
    if (!num) return;
    try {
      setLoading(true);
      setError(null);
      setPreview(null);
      setResult(null);
      const t = await ticketsApi.getByNumber(num);
      setPreview(t);
    } catch (e) {
      if (e.response?.status === 403) {
        setError("You don't have permission to view ticket details");
      } else {
        const msg = e.response?.data?.message;
        setError(
          Array.isArray(msg) ? msg.join(", ") : msg || "Ticket not found",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Check-in from preview ──────────────────────────────────────────────────
  const doCheckInFromPreview = async () => {
    if (!canCheckInTickets) {
      message.error("You don't have permission to check in passengers");
      return;
    }

    if (!preview) return;
    try {
      setLoading(true);
      setError(null);
      const r = await ticketsApi.scan(
        JSON.stringify({
          ticketId: preview.id,
          ticketNumber: preview.ticketNumber,
          reservationId: preview.reservationId,
        }),
      );
      setResult(r);
      setPreview(null);
      message.success("Passenger checked in!");
    } catch (e) {
      if (e.response?.status === 403) {
        setError("You don't have permission to check in passengers");
      } else {
        const msg = e.response?.data?.message;
        setError(
          Array.isArray(msg) ? msg.join(", ") : msg || "Check-in failed",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setQrInput("");
    setPreview(null);
    setTicketNum("");
    if (tab === "camera") {
      setScanning(true);
      setTimeout(() => startScanLoop(), 200);
    } else setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleKeyDownQR = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      doScan(qrInput);
    }
  };

  // Permission check for scan page
  if (!canAccessScan) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to scan tickets for check-in.</p>
                  <p className="mt-2">
                    <strong>Required permission:</strong> tickets.check_in
                  </p>
                  <p className="mt-2">
                    <strong>Your permissions:</strong>{" "}
                    {userPermissions.length > 0
                      ? userPermissions.join(", ")
                      : "None"}
                  </p>
                </div>
              }
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  const rd = result?.reservationDetails;

  const statusBadge = (ticket) => {
    if (!ticket) return null;
    const rs = ticket.reservation?.status;
    if (ticket.isCheckedIn)
      return { label: "Already Checked In", color: "#059669", bg: "#dcfce7" };
    if (ticket.isCancelled || rs === "cancelled")
      return { label: "Cancelled", color: "#dc2626", bg: "#fee2e2" };
    if (rs === "refunded")
      return { label: "Refunded", color: "#d97706", bg: "#fef3c7" };
    if (rs === "pending")
      return { label: "Awaiting Payment", color: "#6b7280", bg: "#f3f4f6" };
    return {
      label: "Active — Ready to Board",
      color: "#7B4D12",
      bg: "#F5ECD7",
    };
  };

  const isCheckInAllowed = (ticket) => {
    if (!ticket) return false;
    const rs = ticket.reservation?.status;
    return (
      !ticket.isCheckedIn &&
      !ticket.isCancelled &&
      rs !== "cancelled" &&
      rs !== "refunded" &&
      rs !== "pending"
    );
  };

  // ── Ticket detail rows ─────────────────────────────────────────────────
  const DetailRow = ({ label, value }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "9px 0",
        borderBottom: "1px solid #f3f4f6",
        fontSize: isMobile ? 13 : 14,
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      <span
        style={{
          color: "#9ca3af",
          fontSize: isMobile ? 10 : 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: ".04em",
        }}
      >
        {label}
      </span>
      <strong style={{ color: "#1a1d2e" }}>{value ?? "—"}</strong>
    </div>
  );

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="content-page-header">
              <h5>QR Boarding Scan</h5>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className={isMobile ? "col-12" : "col-lg-7 col-md-10"}>
              {/* ── Tab switcher ── */}
              <div
                style={{
                  display: "flex",
                  gap: 0,
                  marginBottom: 16,
                  background: "#f3f4f6",
                  borderRadius: 10,
                  padding: 4,
                }}
              >
                {[
                  { key: "camera", label: "Camera Scan" },
                  { key: "number", label: "Ticket No." },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => {
                      setTab(t.key);
                      setResult(null);
                      setError(null);
                      setPreview(null);
                      setQrInput("");
                      setTicketNum("");
                    }}
                    style={{
                      flex: 1,
                      padding: isMobile ? "10px 0" : "8px 0",
                      border: "none",
                      cursor: "pointer",
                      borderRadius: 8,
                      fontSize: isMobile ? 14 : 13,
                      fontWeight: tab === t.key ? 600 : 400,
                      background: tab === t.key ? "#fff" : "transparent",
                      color: tab === t.key ? "#7B4D12" : "#6b7280",
                      boxShadow:
                        tab === t.key ? "0 1px 4px rgba(0,0,0,.1)" : "none",
                      fontFamily: "inherit",
                      transition: "all .15s",
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* ── Camera tab ── */}
              {tab === "camera" && (
                <div className="card mb-0" style={{ borderRadius: 14 }}>
                  <div
                    className="card-body"
                    style={{ padding: 0, overflow: "hidden", borderRadius: 14 }}
                  >
                    {/* Video viewport */}
                    <div
                      style={{
                        position: "relative",
                        background: "#0f172a",
                        borderRadius: "14px 14px 0 0",
                        overflow: "hidden",
                        minHeight: isMobile ? 280 : 360,
                      }}
                    >
                      <video
                        ref={videoRef}
                        muted
                        playsInline
                        style={{
                          width: "100%",
                          display: "block",
                          maxHeight: isMobile ? 360 : 480,
                          objectFit: "cover",
                        }}
                      />
                      <canvas ref={canvasRef} style={{ display: "none" }} />

                      {/* Scanning overlay */}
                      {cameraOn && scanning && !result && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                          }}
                        >
                          {/* Corner brackets */}
                          {[
                            {
                              top: "50%",
                              left: "50%",
                              transform: `translate(${
                                isMobile ? "-70px" : "-90px"
                              },${isMobile ? "-70px" : "-90px"})`,
                              borderTop: `3px solid #fff`,
                              borderLeft: `3px solid #fff`,
                              borderRadius: "4px 0 0 0",
                            },
                            {
                              top: "50%",
                              left: "50%",
                              transform: `translate(${
                                isMobile ? "46px" : "66px"
                              },${isMobile ? "-70px" : "-90px"})`,
                              borderTop: `3px solid #fff`,
                              borderRight: `3px solid #fff`,
                              borderRadius: "0 4px 0 0",
                            },
                            {
                              top: "50%",
                              left: "50%",
                              transform: `translate(${
                                isMobile ? "-70px" : "-90px"
                              },${isMobile ? "46px" : "66px"})`,
                              borderBottom: `3px solid #fff`,
                              borderLeft: `3px solid #fff`,
                              borderRadius: "0 0 0 4px",
                            },
                            {
                              top: "50%",
                              left: "50%",
                              transform: `translate(${
                                isMobile ? "46px" : "66px"
                              },${isMobile ? "46px" : "66px"})`,
                              borderBottom: `3px solid #fff`,
                              borderRight: `3px solid #fff`,
                              borderRadius: "0 0 4px 0",
                            },
                          ].map((s, i) => (
                            <div
                              key={i}
                              style={{
                                position: "absolute",
                                width: isMobile ? 20 : 24,
                                height: isMobile ? 20 : 24,
                                ...s,
                              }}
                            />
                          ))}
                          {/* Scan line animation */}
                          <div
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: `translate(${
                                isMobile ? "-67px" : "-87px"
                              }, ${isMobile ? "-67px" : "-87px"})`,
                              width: isMobile ? 134 : 174,
                              height: isMobile ? 134 : 174,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                height: 2,
                                background:
                                  "linear-gradient(90deg, transparent, #7B4D12, transparent)",
                                animation: "scanline 1.5s linear infinite",
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Camera error */}
                      {cameraError && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 24,
                            textAlign: "center",
                            background: "rgba(0,0,0,0.7)",
                          }}
                        >
                          <div style={{ fontSize: 32, marginBottom: 12 }}>
                            📷
                          </div>
                          <div
                            style={{
                              color: "#fff",
                              fontSize: isMobile ? 13 : 14,
                              marginBottom: 16,
                            }}
                          >
                            {cameraError}
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={startCamera}
                            style={{ fontSize: isMobile ? 13 : 14 }}
                          >
                            Try Again
                          </button>
                        </div>
                      )}

                      {/* Loading indicator */}
                      {loading && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(15,23,42,0.7)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            gap: 12,
                          }}
                        >
                          <span className="spinner-border text-light" />
                          <span style={{ color: "#fff", fontSize: 13 }}>
                            Checking in…
                          </span>
                        </div>
                      )}

                      {/* Torch button */}
                      {cameraOn && (
                        <button
                          type="button"
                          onClick={toggleTorch}
                          style={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            background: torchOn
                              ? "#fbbf24"
                              : "rgba(255,255,255,0.15)",
                            border: "none",
                            borderRadius: 8,
                            padding: isMobile ? "8px 12px" : "6px 10px",
                            cursor: "pointer",
                            fontSize: isMobile ? 18 : 16,
                            color: torchOn ? "#1a1d2e" : "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                          title={torchOn ? "Turn off torch" : "Turn on torch"}
                        >
                          🔦
                        </button>
                      )}
                    </div>

                    {/* Camera status bar */}
                    <div
                      style={{
                        padding: isMobile ? "12px 16px" : "14px 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: scanning
                              ? "#22c55e"
                              : cameraError
                              ? "#ef4444"
                              : "#9ca3af",
                            animation: scanning
                              ? "pulse 1.5s infinite"
                              : "none",
                          }}
                        />
                        <span
                          style={{
                            fontSize: isMobile ? 12 : 13,
                            color: "#6b7280",
                          }}
                        >
                          {scanning
                            ? "Scanning for QR codes…"
                            : cameraError
                            ? "Camera unavailable"
                            : "Initialising…"}
                        </span>
                      </div>
                      {!scanning && !cameraError && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{
                            padding: isMobile ? "6px 16px" : "4px 12px",
                            fontSize: isMobile ? 12 : 12,
                          }}
                          onClick={startCamera}
                        >
                          Start Camera
                        </button>
                      )}
                    </div>

                    <style>{`
                      @keyframes scanline { 0%{transform:translateY(0)} 50%{transform:translateY(${
                        isMobile ? "130px" : "170px"
                      })} 100%{transform:translateY(0)} }
                      @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
                    `}</style>
                  </div>
                </div>
              )}

              {/* ── Ticket number tab ── */}
              {tab === "number" && (
                <div className="card mb-0" style={{ borderRadius: 14 }}>
                  <div
                    className="card-body"
                    style={{ padding: isMobile ? "20px" : "28px 28px 24px" }}
                  >
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                      <div
                        style={{
                          width: isMobile ? 48 : 56,
                          height: isMobile ? 48 : 56,
                          borderRadius: 14,
                          margin: "0 auto 12px",
                          background: "#7B4D12",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: isMobile ? 20 : 22,
                          color: "#fff",
                        }}
                      >
                        🎫
                      </div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: isMobile ? 14 : 15,
                          color: "#1a1d2e",
                          marginBottom: 4,
                        }}
                      >
                        Look Up by Ticket Number
                      </div>
                      <div
                        style={{
                          color: "#9ca3af",
                          fontSize: isMobile ? 12 : 13,
                        }}
                      >
                        Enter the ticket number to view details and check in.
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        marginBottom: 16,
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <input
                        ref={inputRef}
                        className="form-control"
                        placeholder="TC-20260322-XXXXX"
                        value={ticketNum}
                        onChange={(e) =>
                          setTicketNum(e.target.value.toUpperCase())
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleLookup();
                        }}
                        disabled={loading}
                        style={{
                          fontFamily: "monospace",
                          fontSize: isMobile ? 13 : 14,
                          letterSpacing: 1,
                          borderRadius: 10,
                          borderColor: "#ddd9f3",
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleLookup}
                        disabled={loading || !ticketNum.trim()}
                        style={{
                          flexShrink: 0,
                          width: isMobile ? "100%" : "auto",
                        }}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          "Look Up"
                        )}
                      </button>
                    </div>
                    {(result || error || preview) && (
                      <button
                        type="button"
                        className="btn btn-primary cancel"
                        onClick={handleReset}
                        disabled={loading}
                        style={{ marginBottom: 8, width: "100%" }}
                      >
                        ← New Search
                      </button>
                    )}
                    {error && (
                      <Alert
                        message={error}
                        type="error"
                        showIcon
                        className="mt-3"
                        style={{ borderRadius: 10 }}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* ── Camera scan error (below camera card) ── */}
              {tab === "camera" && error && !result && (
                <div
                  className="card mt-3"
                  style={{ borderRadius: 14, border: "2px solid #fecaca" }}
                >
                  <div
                    className="card-body"
                    style={{ padding: isMobile ? "16px" : "20px 24px" }}
                  >
                    <Alert
                      message={error}
                      type="error"
                      showIcon
                      style={{ borderRadius: 8, marginBottom: 14 }}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ width: "100%" }}
                      onClick={resumeScan}
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}

              {/* ── Ticket preview ── */}
              {preview &&
                !result &&
                (() => {
                  const badge = statusBadge(preview);
                  const allow = isCheckInAllowed(preview);
                  const r = preview.reservation;
                  const trip = r?.tripDate?.trip;
                  return (
                    <div
                      className="card mt-3"
                      style={{ borderRadius: 14, border: "2px solid #F5ECD7" }}
                    >
                      <div
                        className="card-body"
                        style={{ padding: isMobile ? "20px" : "24px 28px" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            marginBottom: 20,
                            flexWrap: "wrap",
                            gap: 12,
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: isMobile ? 10 : 11,
                                fontWeight: 700,
                                letterSpacing: ".06em",
                                textTransform: "uppercase",
                                color: "#9ca3af",
                                marginBottom: 4,
                              }}
                            >
                              Ticket
                            </div>
                            <code
                              style={{
                                fontSize: isMobile ? 16 : 20,
                                fontWeight: 700,
                                color: "#7B4D12",
                                background: "#F5ECD7",
                                padding: "4px 12px",
                                borderRadius: 8,
                              }}
                            >
                              {preview.ticketNumber}
                            </code>
                          </div>
                          <span
                            style={{
                              padding: "4px 12px",
                              borderRadius: 20,
                              fontSize: isMobile ? 11 : 12,
                              fontWeight: 600,
                              background: badge.bg,
                              color: badge.color,
                            }}
                          >
                            {badge.label}
                          </span>
                        </div>
                        {preview.qrCode && (
                          <div
                            style={{
                              textAlign: "center",
                              marginBottom: 20,
                              padding: "16px 0",
                              background: "#f8fafc",
                              borderRadius: 10,
                            }}
                          >
                            <img
                              src={preview.qrCode}
                              alt="QR Code"
                              style={{
                                width: isMobile ? 140 : 160,
                                height: isMobile ? 140 : 160,
                                borderRadius: 8,
                                border: "1px solid #e5e7eb",
                              }}
                            />
                            <div
                              style={{
                                fontSize: isMobile ? 10 : 11,
                                color: "#9ca3af",
                                marginTop: 6,
                              }}
                            >
                              Boarding QR Code
                            </div>
                          </div>
                        )}
                        <DetailRow
                          label="Passenger"
                          value={
                            r?.user
                              ? `${r.user.firstName} ${r.user.lastName}`
                              : r?.guestName || "Guest"
                          }
                        />
                        <DetailRow label="Trip" value={trip?.name || "—"} />
                        <DetailRow
                          label="Date"
                          value={r?.tripDate?.date || "—"}
                        />
                        <DetailRow
                          label="Time"
                          value={
                            trip?.startTime && trip?.endTime
                              ? `${trip.startTime} – ${trip.endTime}`
                              : "—"
                          }
                        />
                        <DetailRow label="Deck" value={r?.deck?.name || "—"} />
                        <DetailRow label="Seats" value={r?.seats ?? "—"} />
                        <div style={{ marginTop: 20 }}>
                          {allow ? (
                            <button
                              type="button"
                              className="btn btn-primary"
                              style={{
                                width: "100%",
                                height: isMobile ? 40 : 44,
                                fontSize: isMobile ? 14 : 15,
                              }}
                              onClick={doCheckInFromPreview}
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" />
                                  Checking in…
                                </>
                              ) : (
                                <>
                                  <CheckCircleOutlined
                                    style={{ marginRight: 8 }}
                                  />
                                  Confirm Check-In
                                </>
                              )}
                            </button>
                          ) : (
                            <Alert
                              message={`Cannot check in: ${badge.label}`}
                              type={preview.isCheckedIn ? "success" : "warning"}
                              showIcon
                              style={{ borderRadius: 8 }}
                            />
                          )}
                        </div>
                        {preview.isCheckedIn && preview.checkedInAt && (
                          <div
                            style={{
                              marginTop: 10,
                              textAlign: "center",
                              fontSize: isMobile ? 11 : 12,
                              color: "#9ca3af",
                            }}
                          >
                            Checked in at{" "}
                            {new Date(preview.checkedInAt).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

              {/* ── Success result ── */}
              {result && rd && (
                <div
                  className="card mt-3"
                  style={{ borderRadius: 14, border: "2px solid #bbf7d0" }}
                >
                  <div
                    className="card-body"
                    style={{ padding: isMobile ? "20px" : "24px 28px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 20,
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          width: isMobile ? 40 : 48,
                          height: isMobile ? 40 : 48,
                          borderRadius: 12,
                          flexShrink: 0,
                          background: "#dcfce7",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircleOutlined
                          style={{
                            fontSize: isMobile ? 20 : 24,
                            color: "#16a34a",
                          }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: isMobile ? 14 : 16,
                            color: "#15803d",
                          }}
                        >
                          Checked In Successfully
                        </div>
                        <div
                          style={{
                            fontSize: isMobile ? 11 : 12,
                            color: "#9ca3af",
                          }}
                        >
                          {result.ticket?.ticketNumber}
                        </div>
                      </div>
                    </div>
                    <DetailRow label="Passenger" value={rd.passengerName} />
                    <DetailRow label="Trip" value={rd.tripName} />
                    <DetailRow label="Date" value={rd.date} />
                    <DetailRow label="Deck" value={rd.deck} />
                    <DetailRow label="Seats" value={rd.seats} />
                    {result.ticket?.checkedInAt && (
                      <div
                        style={{
                          marginTop: 12,
                          textAlign: "right",
                          fontSize: isMobile ? 11 : 12,
                          color: "#9ca3af",
                        }}
                      >
                        Checked in at{" "}
                        {new Date(result.ticket.checkedInAt).toLocaleString()}
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ width: "100%", marginTop: 16 }}
                      onClick={handleReset}
                    >
                      Scan Next Passenger
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
